import { useState } from "react";
import { cn } from "@/lib/utils";

const AVATAR_COLORS = [
  "bg-indigo-500",
  "bg-violet-500",
  "bg-emerald-500",
  "bg-amber-500",
  "bg-rose-500",
  "bg-sky-500",
  "bg-teal-500",
  "bg-orange-500",
];

function getInitials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase() ?? "")
    .join("");
}

function getAvatarColor(name: string): string {
  const hash = name.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

interface AvatarWithInitialsProps {
  src?: string | null;
  name: string;
  className?: string;
}

/**
 * Affiche l'avatar si disponible, sinon les initiales générées à partir du nom.
 * Les classes Tailwind de taille, bordure, ombre, etc. sont passées via `className`
 * et s'appliquent à l'image ET au conteneur de repli (initiales).
 * Inclure une classe `text-X` dans `className` pour dimensionner les initiales
 * (ignorée sur l'image, appliquée sur le div de repli).
 */
export function AvatarWithInitials({ src, name, className }: AvatarWithInitialsProps) {
  const [hasError, setHasError] = useState(false);

  if (src && !hasError) {
    return <img src={src} alt={name} className={className} onError={() => setHasError(true)} />;
  }

  return (
    <div
      className={cn(
        "flex items-center justify-center text-white font-black uppercase tracking-tight select-none",
        getAvatarColor(name),
        className,
      )}
    >
      {getInitials(name)}
    </div>
  );
}
